import React, { useState } from "react";
import { toast } from "sonner";
import { FileText, Music, Film, Image as ImageIcon, Download, Trash2, Share2, File as FileIcon } from "lucide-react";
import { media as M } from "../lib/api";

const fmtSize = (b) => {
  if (!b) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

function KindIcon({ kind, size = 22 }) {
  if (kind === "image") return <ImageIcon size={size} />;
  if (kind === "video") return <Film size={size} />;
  if (kind === "audio") return <Music size={size} />;
  if (kind === "document") return <FileText size={size} />;
  return <FileIcon size={size} />;
}

function Preview({ a }) {
  const url = M.url(a.id);
  if (a.kind === "image") return <img src={url} alt={a.caption || a.original_filename} className="w-full h-40 object-cover rounded-t" data-testid={`media-img-${a.id}`} />;
  if (a.kind === "video") return <video src={url} controls className="w-full h-40 object-cover rounded-t bg-black" data-testid={`media-video-${a.id}`} />;
  if (a.kind === "audio") return (
    <div className="p-4 flex flex-col items-center justify-center h-40" style={{ background: "var(--surface-2)" }}>
      <Music size={30} className="mb-2" />
      <audio src={url} controls className="w-full" data-testid={`media-audio-${a.id}`} />
    </div>
  );
  return (
    <div className="flex items-center justify-center h-40" style={{ background: "var(--surface-2)" }}>
      <KindIcon kind={a.kind} size={44} />
    </div>
  );
}

// Reusable gallery for a set of media assets.
// Props: assets [], onChanged(), canManage (bool), compact (bool)
export default function MediaGallery({ assets = [], onChanged, canManage = true, compact = false }) {
  const [shareFor, setShareFor] = useState(null);
  const [email, setEmail] = useState("");

  const del = async (a) => {
    if (!window.confirm(`Delete "${a.original_filename}"?`)) return;
    try { await M.del(a.id); toast.success("Deleted"); onChanged && onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const toggleVis = async (a) => {
    const next = a.visibility === "Shared" ? "Private" : "Shared";
    try { await M.patch(a.id, { visibility: next }); toast.success(`Set to ${next}`); onChanged && onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const doShare = async () => {
    if (!email.trim()) return;
    try { await M.share(shareFor.id, email.trim()); toast.success(`Shared with ${email}`); setShareFor(null); setEmail(""); onChanged && onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!assets.length) return <div className="text-sm" style={{ color: "var(--muted)" }} data-testid="media-empty">No files yet.</div>;

  if (compact) {
    return (
      <div className="flex flex-wrap gap-2" data-testid="media-gallery-compact">
        {assets.map((a) => (
          <a key={a.id} href={M.url(a.id)} target="_blank" rel="noreferrer"
             className="flex items-center gap-1.5 text-xs px-2 py-1 rounded border" style={{ borderColor: "var(--border)", background: "var(--surface-2)" }}
             data-testid={`media-chip-${a.id}`}>
            <KindIcon kind={a.kind} size={13} /> {a.original_filename}
          </a>
        ))}
      </div>
    );
  }

  return (
    <>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="media-gallery">
        {assets.map((a) => (
          <div key={a.id} className="card p-0 overflow-hidden" data-testid={`media-card-${a.id}`}>
            <Preview a={a} />
            <div className="p-3">
              <div className="flex items-center gap-2">
                <KindIcon kind={a.kind} size={16} />
                <div className="text-sm font-medium truncate flex-1" title={a.original_filename}>{a.caption || a.original_filename}</div>
              </div>
              <div className="text-xs mt-1" style={{ color: "var(--muted)" }}>
                {fmtSize(a.size)} · <span className={`chip ${a.visibility === "Shared" ? "chip-green" : "chip-gray"}`}>{a.visibility}</span>
              </div>
              <div className="flex gap-3 mt-3 text-xs">
                <a href={M.url(a.id)} target="_blank" rel="noreferrer" className="flex items-center gap-1" data-testid={`media-download-${a.id}`}><Download size={13} /> Open</a>
                {canManage && a._is_owner && (
                  <>
                    <button className="flex items-center gap-1" onClick={() => toggleVis(a)} data-testid={`media-vis-${a.id}`}><Share2 size={13} /> {a.visibility === "Shared" ? "Make private" : "Make shared"}</button>
                    <button className="flex items-center gap-1" onClick={() => setShareFor(a)} data-testid={`media-shareto-${a.id}`}>Share to…</button>
                    <button className="flex items-center gap-1" style={{ color: "var(--overdue)" }} onClick={() => del(a)} data-testid={`media-delete-${a.id}`}><Trash2 size={13} /> Delete</button>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      {shareFor && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white p-6 rounded max-w-md w-full">
            <div className="font-display text-xl mb-2">Share "{shareFor.original_filename}"</div>
            <input placeholder="person@example.com" value={email} onChange={(e) => setEmail(e.target.value)} className="mb-3" data-testid="media-share-email" />
            <div className="flex gap-2 justify-end">
              <button className="btn-outline" onClick={() => { setShareFor(null); setEmail(""); }}>Cancel</button>
              <button className="btn-primary" onClick={doShare} data-testid="media-share-confirm">Share</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
